import type { FunctionComponent } from 'react';
import { useCallback } from 'react';

import CalendarPlusIcon from '@atlaskit/icon/core/calendar-plus';
import { LabsLozenge } from '@trello/ai-labs';
import { Analytics } from '@trello/atlassian-analytics';
import {
  smartScheduledCardsSharedState,
  type SmartScheduledCardPlannerEvent,
} from '@trello/business-logic/planner';
import { client } from '@trello/graphql';
import { useCardId } from '@trello/id-context';
import { PLANNER_CONSTANTS } from '@trello/planner';
import {
  useIsPlannerPanelOpen,
  useSplitScreenSharedState,
} from '@trello/split-screen';
import { token } from '@trello/theme';

import { SmartScheduleCardFragmentDoc } from 'app/src/components/BulkAction/SmartScheduleCardFragment.generated';
import { useSmartScheduleCardsMutation } from 'app/src/components/BulkAction/SmartScheduleCardsMutation.generated';
import { QuickCardEditorButton } from './QuickCardEditorButton';

import * as styles from './ScheduleCardButton.module.less';

interface ScheduleCardButtonProps {
  onClose: () => void;
}

export const ScheduleCardButton: FunctionComponent<ScheduleCardButtonProps> = ({
  onClose,
}) => {
  const cardId = useCardId();
  const [smartScheduleCards] = useSmartScheduleCardsMutation();
  const isPlannerPanelOpen = useIsPlannerPanelOpen();
  const { togglePlanner } = useSplitScreenSharedState();

  const handleSchedule = useCallback(() => {
    smartScheduledCardsSharedState.setValue({
      ...smartScheduledCardsSharedState.value,
      isLoading: true,
    });

    onClose();

    const offsetMinutes = new Date().getTimezoneOffset();
    const offsetHours = -offsetMinutes / 60;

    const traceId = Analytics.startTask({
      taskName: 'edit-plannerEvent/smartSchedule',
      source: 'quickEditOverlay',
    });

    smartScheduleCards({
      variables: {
        cardIds: [cardId],
        timezoneOffsetHours: offsetHours,
        traceId,
      },
    })
      .then(({ data }) => {
        const proposedEvents = data?.smartScheduleCards?.scheduled_cards?.map(
          (card) => {
            const cardData = client.readFragment({
              id: `Card:${card.card_id}`,
              fragment: SmartScheduleCardFragmentDoc,
            });
            return {
              id: card.card_id,
              start: card.start_time,
              end: card.end_time,
              eventId:
                PLANNER_CONSTANTS.PROPOSED_EVENT_ID_PREFIX + card.card_id,
              title: cardData?.name,
              durationEditable: true,
              startEditable: true,
            } as SmartScheduledCardPlannerEvent;
          },
        );
        smartScheduledCardsSharedState.setValue({
          activeIndex: 0,
          isLoading: false,
          proposedEvents: [
            ...smartScheduledCardsSharedState.value.proposedEvents,
            ...(proposedEvents ?? []),
          ],
        });

        Analytics.taskSucceeded({
          taskName: 'edit-plannerEvent/smartSchedule',
          traceId,
          source: 'quickEditOverlay',
        });
        if (proposedEvents?.length && !isPlannerPanelOpen) {
          togglePlanner();
        }
      })
      .catch((error) => {
        Analytics.taskFailed({
          taskName: 'edit-plannerEvent/smartSchedule',
          traceId,
          source: 'quickEditOverlay',
          error,
        });

        smartScheduledCardsSharedState.setValue({
          ...smartScheduledCardsSharedState.value,
          isLoading: false,
        });
      });
  }, [cardId, smartScheduleCards, isPlannerPanelOpen, togglePlanner, onClose]);

  return (
    <QuickCardEditorButton
      icon={<CalendarPlusIcon label="Schedule" />}
      style={{
        display: 'flex',
        gap: token('space.100', '8px'),
      }}
      onClick={handleSchedule}
      className={styles.scheduleButton}
    >
      <span className={styles.scheduleButtonText}>
        {/* TODO: i18n */}
        Schedule
        <LabsLozenge />
      </span>
    </QuickCardEditorButton>
  );
};
